import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Container, MainHeading } from '../../globalStyles';
import { 
  HeroCarouselContainer, 
  SloganSection,
  SloganTitle,
  SloganSubtitle,
  CarouselSection,
  HeroSlide, 
  HeroSlideContainer,
  HeroImageContainer,
  HeroSlideContent, 
  HeroText, 
  ButtonWrapper, 
  HeroButton,
  CarouselDots,
  Dot,
  CarouselArrow,
  HeroImage
} from './HeroCarouselStyles';
import { featuresData } from '../../data/FeaturesData';

export default function HeroCarousel() {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-advance slides
  useEffect(() => {
    if (isPaused) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuresData.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, [isPaused]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsPaused(true);
    // Resume auto-advance after 8 seconds of inactivity
    setTimeout(() => setIsPaused(false), 8000);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + featuresData.length) % featuresData.length);
    setIsPaused(true);
    // Resume auto-advance after 8 seconds of inactivity
    setTimeout(() => setIsPaused(false), 8000);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % featuresData.length);
    setIsPaused(true);
    // Resume auto-advance after 8 seconds of inactivity
    setTimeout(() => setIsPaused(false), 8000);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }
  };

  return (
    <HeroCarouselContainer
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {featuresData.map((feature, index) => (
        <HeroSlide 
          key={index} 
          className={index === currentSlide ? 'active' : ''}
        >
          <HeroImage src={feature.image} alt={t(`features_data.${index}.name`)} />
          <HeroSlideContent>
            <HeroText>
              <h1>{t(`features_data.${index}.name`)}</h1>
              <p>{t(`features_data.${index}.description`)}</p>
            </HeroText>
            <ButtonWrapper>
              <Link to={feature.link}>
                <Button>{t('read_more') || 'VER MÁS'}</Button>
              </Link>
            </ButtonWrapper>
          </HeroSlideContent>
        </HeroSlide>
      ))}
      
      {/* Navigation arrows */}
      <CarouselArrow className="prev" onClick={goToPrevious} aria-label="Slide anterior">
        ‹
      </CarouselArrow>
      <CarouselArrow className="next" onClick={goToNext} aria-label="Slide siguiente">
        ›
      </CarouselArrow>

      {/* Dots indicator */}
      <CarouselDots>
        {featuresData.map((_, index) => (
          <Dot
            key={index}
            className={index === currentSlide ? 'active' : ''}
            onClick={() => goToSlide(index)}
          />
        ))}
      </CarouselDots>
    </HeroCarouselContainer>
  );
}
